import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import boTheme from '@ecp/common/src/style/theme/bo';
import { FlexBox, FlexBoxCenter, FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import { TextButton } from '@ecp/common/src/components/button/bo/TextButton';
import { Button } from '@ecp/common/src/components/button/bo/Button';

import SearchRow from './SearchRow';
import { ReactComponent as CollapseDown } from '@bo-assets/icon/common/icon__collapse.svg';

const Container = styled(FlexBoxColumn)`
  width: 100%;
  padding: 0 20px;
  border-radius: 6px;
  background-color: ${boTheme.color.table3};
  box-sizing: border-box;
  align-items: stretch;

  & .search-row:not(:last-of-type) {
    border-bottom: 1px solid ${boTheme.color.sub8};
  }

  & .search-row.collapsible {
    display: ${({ collapse }) => (collapse ? 'none' : 'flex')};
  }

  flex-direction: ${({ useRow }) => (useRow ? 'row' : 'column')};
`;

const CollapseButtonWrapper = styled(FlexBoxCenter)`
  flex-grow: 1;
  align-self: stretch;
`;

const SearchButtonContainer = styled(FlexBox)`
  gap: 6px;
  position: absolute;
  ${({ useRow }) => (useRow ? 'bottom: 8px' : 'top: 8px')};
  right: 0;
`;

const CollapseUp = styled(CollapseDown)`
  transform: rotate(180deg);
`;

const Search = ({
  children,
  onClearClick,
  onSearchClick,
  collapsible,
  initialCollapsed,
  clearButtonText,
  searchButtonText,
  showButton,
  useRow,
}) => {
  const [collapse, setCollapse] = useState(initialCollapsed);

  const handleCollapseClick = () => {
    setCollapse((prev) => !prev);
  };

  const handleKeyUp = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        onSearchClick?.(e);
      }
    },
    [onSearchClick]
  );

  return (
    <Container collapse={collapse} onKeyUp={handleKeyUp} useRow={useRow}>
      {children}
      {(collapsible || showButton) && (
        <SearchRow>
          {collapsible && (
            <CollapseButtonWrapper>
              <TextButton
                type={'custom'}
                textProps={{
                  size: boTheme.font.size.small,
                  weight: `${boTheme.font.weight.regular}`,
                  color: boTheme.color.sub2,
                }}
                onClick={handleCollapseClick}
                customIcon={collapse ? CollapseDown : CollapseUp}
              >
                {collapse ? '상세검색 펼치기' : '상세검색 접기'}
              </TextButton>
            </CollapseButtonWrapper>
          )}
          {showButton && (
            <SearchButtonContainer useRow={useRow}>
              <Button type="Third" width="60px" onClick={onClearClick}>
                {clearButtonText}
              </Button>
              <Button type="Secondary" width="60px" onClick={onSearchClick}>
                {searchButtonText}
              </Button>
            </SearchButtonContainer>
          )}
        </SearchRow>
      )}
    </Container>
  );
};

Search.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  onClearClick: PropTypes.func,
  onSearchClick: PropTypes.func,
  collapsible: PropTypes.bool,
  initialCollapsed: PropTypes.bool,
  clearButtonText: PropTypes.string,
  searchButtonText: PropTypes.string,
  showButton: PropTypes.bool,
  useRow: PropTypes.bool,
};

Search.defaultProps = {
  collapsible: false,
  clearButtonText: '초기화',
  searchButtonText: '검색',
  showButton: true,
};

export default Search;
